import { Component, ErrorInfo, ReactNode } from 'react';
import { isDeliveries } from '../../helpers/appType';
import { reportError } from '../../helpers/reportError';

type FallbackRender = (args: {
  error: Error;
  reset: () => void;
}) => ReactNode;

type Props = {
  /**
   * Subtree to protect. Any render-time error thrown by `children` (or their
   * descendants) is caught here.
   */
  children: ReactNode;

  /**
   * Custom fallback UI. Either a static `ReactNode` or a render function that
   * receives the caught error and a `reset()` callback (clears the boundary's
   * error state so the children re-mount).
   *
   * If omitted, a minimal inline-styled fallback is rendered — intentionally
   * dependency-free (no theme / no styled-components) so it still renders
   * even if the styled-components / Theme provider was what threw.
   */
  fallback?: ReactNode | FallbackRender;

  /**
   * Label that appears in the console / Sentry context, e.g. "AdminApp" or
   * "DeliveryDetails". Helps disambiguate stack traces from minified bundles.
   */
  context?: string;

  /**
   * When any value in this array changes between renders, the boundary
   * automatically resets itself (clears the error and re-mounts children).
   * Useful for "reset on route change":
   *
   *   const { pathname } = useLocation();
   *   <ErrorBoundary resetKeys={[pathname]}>...</ErrorBoundary>
   */
  resetKeys?: ReadonlyArray<unknown>;

  /**
   * Called after a manual or auto-reset. Use it to clear any external state
   * that may have caused the error.
   */
  onReset?: () => void;
};

type State = {
  error: Error | null;
};

/**
 * Class-based error boundary — React still requires a class component for
 * this (no hooks equivalent as of React 19).
 *
 * On a render-time error in the subtree:
 *   1. `getDerivedStateFromError` flips state.error → triggers the fallback render
 *   2. `componentDidCatch` forwards to `reportError(error, { context })`,
 *      which routes through the shared toast + console.error + Sentry pipeline
 *
 * Errors thrown in event handlers / async code do NOT pass through here —
 * those are still the responsibility of the surrounding try/catch +
 * `reportError(...)` pattern. This boundary is specifically for *render*
 * errors that would otherwise unmount the whole subtree.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      context: this.props.context ?? 'ErrorBoundary',
      // The toast UI on render-error is usually overkill — the fallback
      // already informs the user — so suppress it here. console.error and
      // Sentry capture still fire.
      silent: true,
    });
    // Preserve componentStack in console for devs; reportError logs the raw
    // error, this gives the React tree context.
    if (info?.componentStack) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary componentStack]', info.componentStack);
    }
  }

  componentDidUpdate(prevProps: Props): void {
    if (!this.state.error) return;
    const { resetKeys } = this.props;
    if (!resetKeys || !prevProps.resetKeys) return;
    const changed =
      resetKeys.length !== prevProps.resetKeys.length ||
      resetKeys.some((k, i) => k !== prevProps.resetKeys?.[i]);
    if (changed) this.reset();
  }

  reset = (): void => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === 'function') {
      return (fallback as FallbackRender)({ error, reset: this.reset });
    }
    if (fallback !== undefined) return fallback;

    return <DefaultFallback error={error} reset={this.reset} />;
  }
}

/**
 * Brand-matched fallback styled inline (no styled-components / theme
 * dependency). The reason for inline styles instead of `Button`/`Div`
 * imports: if styled-components or the ThemeProvider itself was what
 * threw, this still renders. Colors mirror `lightTheme` from
 * components/src/styles/theme.js (background `#f9f4ee`, primary
 * `#E61E29`, darkGray `#4C4C4C`, lightGray `#A9A9A9`). The accent follows
 * the app: deliveries is navy-branded, everything else keeps vGrubs red -
 * read from the appType leaf, not the theme, so this file stays safe to
 * render when the ThemeProvider itself is what broke.
 */
const ACCENT = isDeliveries ? '#172A55' : '#E61E29';
function DefaultFallback({ error }: { error: Error; reset: () => void }) {
  const isDev = process.env.NODE_ENV !== 'production';
  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        fontFamily: 'inherit',
        textAlign: 'center',
        color: '#4C4C4C',
        background: '#f9f4ee',
      }}
    >
      <div
        aria-hidden
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: ACCENT,
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '28px',
          fontWeight: 700,
          lineHeight: 1,
          marginBottom: '20px',
        }}
      >
        !
      </div>
      <h1
        style={{
          fontSize: '20px',
          lineHeight: '28px',
          fontWeight: 700,
          margin: '0 0 8px',
          color: '#4C4C4C',
        }}
      >
        Something went wrong
      </h1>
      <p
        style={{
          margin: '0 0 24px',
          color: '#A9A9A9',
          fontSize: '14px',
          lineHeight: '20px',
          maxWidth: '360px',
        }}
      >
        The error has been reported. Please reload the page.
      </p>
      <button
        type="button"
        onClick={() => {
          if (typeof window !== 'undefined') window.location.reload();
        }}
        style={{
          padding: '12px 28px',
          borderRadius: '999px',
          border: 'none',
          background: ACCENT,
          color: '#ffffff',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          fontFamily: 'inherit',
        }}
      >
        Reload page
      </button>
      {isDev && (
        <details
          style={{
            marginTop: '40px',
            maxWidth: '720px',
            width: '100%',
            textAlign: 'left',
          }}
        >
          <summary
            style={{
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 500,
              color: '#A9A9A9',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              padding: '4px',
            }}
          >
            Error details (dev only)
          </summary>
          <pre
            style={{
              marginTop: '12px',
              padding: '16px',
              background: '#ffffff',
              border: '1px solid #e3e3e3',
              borderRadius: '12px',
              maxHeight: '320px',
              overflow: 'auto',
              fontSize: '12px',
              color: ACCENT,
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {error.name}: {error.message}
            {error.stack ? `\n\n${error.stack}` : ''}
          </pre>
        </details>
      )}
    </div>
  );
}
